import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useHandleApiError from '../../../hooks/useHandleApiError';
import useHandleAppException, {
  type AppExceptionResponse,
  AppExceptionResponseData,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { CreateCatalogPayload } from '../../../modules/catalogs/creation/catalogCreation.types';
import { catalogsRepository } from '../../../modules/catalogs/repositories/catalogs.repository';
import {
  BatchUpdateCatalogSectionsParams,
  UpdateCatalogNavigationPreferencesParams,
  UpdateCatalogParams,
  UpdateCatalogViewPreferencesParams,
} from '../../../modules/catalogs/repositories/types/catalogs.repository.types';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { getGetCatalogKey } from './catalogs.keys';
import { createCatalog } from './catalogs.repository';

interface UseUpdateCatalogParams {
  catalogId: string;
}

interface UseUpdateCatalogViewPreferencesParams {
  catalogId: string;
}

interface UseUpdateCatalogNavigationPreferencesParams {
  catalogId: string;
}

interface UseBatchUpdateCatalogSectionsParams {
  catalogId: string;
}

interface UseSyncDynamicCatalogParams {
  catalogId: string;
}

interface InvalidateCatalogGroupingQueriesParams {
  queryClient: QueryClient;
  teamId?: string;
  catalogId: string;
}

const invalidateCatalogGroupingQueries = ({
  queryClient,
  teamId,
  catalogId,
}: InvalidateCatalogGroupingQueriesParams) => {
  queryClient.invalidateQueries({ queryKey: [teamId, ...getGetCatalogKey(catalogId)] });
  queryClient.invalidateQueries({ queryKey: [teamId, 'catalog-products', catalogId] });
  queryClient.invalidateQueries({
    queryKey: [teamId, 'catalogs/:id/products-count', catalogId],
  });
};

interface UseCreateCatalogMutationParams {
  onSuccess: (catalogId: string) => void;
}

export const useCreateCatalogMutation = ({ onSuccess }: UseCreateCatalogMutationParams) => {
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();

  const { isPending, mutate } = useMutation({
    mutationKey: ['catalogs', 'create'],
    mutationFn: async (payload: CreateCatalogPayload) => {
      const response = await createCatalog(payload);

      if (!response.ok || !response.data) {
        throw response.originalError;
      }

      return response.data;
    },
    onSuccess: (createdCatalog) => {
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.GLOBAL_SAVED_SUCCESSFULLY),
      });
      onSuccess(createdCatalog.id);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: AppExceptionResponse } | undefined;
      const message = apiError?.response
        ? handleErrorResponse(apiError.response)
        : translate(LangEnum.GLOBAL_ERROR);

      snackbarStore.handleMessage({ type: 'error', message });
    },
  });

  return { isCreatingCatalog: isPending, createCatalog: mutate };
};

export const useUpdateCatalog = ({ catalogId }: UseUpdateCatalogParams) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const teamId = team?.id;

  return useMutation({
    scope: { id: catalogId },
    mutationFn: async (payload: UpdateCatalogParams) => {
      const response = await catalogsRepository(catalogId).updateCatalog(payload);

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error(LangEnum.GLOBAL_ERROR_MESSAGE);
      }

      return response.data;
    },
    onSuccess: (catalog) => {
      queryClient.setQueryData([teamId, ...getGetCatalogKey(catalogId)], catalog);
      invalidateCatalogGroupingQueries({ queryClient, teamId, catalogId });
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
  });
};

export const useUpdateCatalogNavigationPreferences = ({
  catalogId,
}: UseUpdateCatalogNavigationPreferencesParams) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const teamId = team?.id;

  return useMutation({
    scope: { id: catalogId },
    mutationFn: async (payload: UpdateCatalogNavigationPreferencesParams) => {
      const response = await catalogsRepository(catalogId).updateNavigationPreferences(payload);

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error(LangEnum.GLOBAL_ERROR_MESSAGE);
      }

      return response.data;
    },
    onSuccess: (catalog) => {
      queryClient.setQueryData([teamId, ...getGetCatalogKey(catalogId)], catalog);
      invalidateCatalogGroupingQueries({ queryClient, teamId, catalogId });
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
  });
};

export const useUpdateCatalogViewPreferences = ({
  catalogId,
}: UseUpdateCatalogViewPreferencesParams) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const teamId = team?.id;

  return useMutation({
    scope: { id: catalogId },
    mutationFn: async (payload: UpdateCatalogViewPreferencesParams) => {
      const response = await catalogsRepository(catalogId).updateViewPreferences(payload);

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error(LangEnum.GLOBAL_ERROR_MESSAGE);
      }

      return response.data;
    },
    onSuccess: (catalog, payload) => {
      queryClient.setQueryData([teamId, ...getGetCatalogKey(catalogId)], catalog);

      if (
        payload.sectionSortCriterion === undefined &&
        payload.sortTargetId === undefined &&
        payload.viewGroupBy === undefined &&
        payload.viewGroupTargetId === undefined
      ) {
        return;
      }

      invalidateCatalogGroupingQueries({ queryClient, teamId, catalogId });

      // the catalog list is NOT invalidated: none of the new fields are rendered there, and its
      // key is not prefix-invalidatable — useInfiniteDataQuery builds
      // [team?.id, limit, defaultOrderBy, 'infinite-list-with-checkbox', 'catalogs', params].
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
  });
};

export const useBatchUpdateCatalogSections = ({
  catalogId,
}: UseBatchUpdateCatalogSectionsParams) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  return useMutation({
    scope: { id: catalogId },
    mutationFn: async (payload: BatchUpdateCatalogSectionsParams) => {
      const response = await catalogsRepository(catalogId).batchUpdateCatalogSections(payload);

      if (!response.ok) {
        throw response.originalError ?? new Error(LangEnum.GLOBAL_ERROR_MESSAGE);
      }
    },
    onSuccess: () => {
      invalidateCatalogGroupingQueries({ queryClient, teamId: team?.id, catalogId });
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
  });
};

export const useSyncDynamicCatalog = ({ catalogId }: UseSyncDynamicCatalogParams) => {
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  return useMutation({
    scope: { id: catalogId },
    mutationFn: async () => {
      const response = await catalogsRepository(catalogId).syncDynamicCatalog();

      if (!response.ok) {
        throw response.originalError ?? new Error(LangEnum.CATALOG_SYNC_ERROR);
      }
    },
    onSuccess: () => {
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.CATALOG_SYNC_SUCCESS),
      });
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.CATALOG_SYNC_ERROR)),
      });
    },
  });
};
